import React, { PropsWithChildren } from 'react';
import { Route } from 'react-router-dom';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';

import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis, logtoOIDCAuthApiRef } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root, RootWithLandingPageView } from './components/Root/Root';

import { AlertDisplay, OAuthRequestDialog } from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';

import { AnnouncementsPage } from '@procore-oss/backstage-plugin-announcements';
import { shortcutsPlugin } from '@backstage-community/plugin-shortcuts';
import { DevToolsPage } from '@backstage/plugin-devtools';
import { customDevToolsPage } from './components/devtools/customDevToolsPage';
import { simpleIconsColor } from '@dweber019/backstage-plugin-simple-icons';
import { UnifiedThemeProvider } from '@backstage/theme';
import {
  configApiRef,
  SignInPageProps,
  useApi,
} from '@backstage/core-plugin-api';
import { HomepageCompositionRoot, VisitListener } from '@backstage/plugin-home';
import { dlrTheme } from './theme/dlr';
import {
  DxIframeStorybookPage,
  DxIframeSystemStatusPage,
} from '@internal/backstage-plugin-dx-iframe';
import './global.css';
import { ToolboxPage } from '@drodil/backstage-plugin-toolbox';
import { RelationsCatalogGraphPage } from '@dweber019/backstage-plugin-relations';
import { badgesPlugin } from '@backstage-community/plugin-badges';
import { OpenFeedbackPage } from '@parsifal-m/backstage-plugin-open-feedback';
import { ThemeProvider } from '@mui/material/styles';
import { announcementsPageTheme } from './theme/announcements';
import { SignInPage as SingleSignInPage } from './components/SignIn/CustomSignInPage';
import { LandingSignInPage } from './components/SignIn/LandingSignInPage';
import UserManagement from './components/userManagement/UserManagement';
import { IdentityProvider } from '@internal/shared';
import CatalogApiDocs from './components/ApiDocs/CatalogApiDocs';
import SettingsPage from './components/user-settings/SettingsPage';
import {
  dxPortalAdvancedFeaturesPermission,
  dxPortalBasicFeaturesPermission,
} from '@internal/shared/src/permission/opa_permissions';
import { IdTokenFieldExtension } from './scaffolder/idToken';
import { SnackbarProvider } from 'notistack';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { scaffolderTranslations } from './translations/scafolderPage';
import { catalogImportTranslations } from './translations/catalogImport';
import UserRequestsPage from './components/userRequests/UserRequestsPage';
import { UserRequestsPermissionGuard } from './components/userRequests/UserRequestsPermissionGuard';
import { CustomCatalogIndex } from './components/catalog/CustomCatalogIndex';
import { CustomErrorPage } from './components/CustomErrorPage';
import ContactUsPage from './components/ContactUs/ContactUsPage';
import { LandingPage } from './components/LangingPage/LandingPage';
import { HomePage } from './components/home/HomePage';
import { PraxoChatWidget } from './components/PraxoChat';
import ApiDefinitionPage from './components/ApiDocs/ApiDefinitionPage';
import DocsPage from './components/DocsV2/DocsPage';
import { DocsArticle } from './components/DocsV2/components/DocsArticle';
import { ScaffolderReviewCleanup } from './scaffolder/ScaffolderPageWithReviewCleanup';

const app = createApp({
  apis,
  __experimentalTranslations: {
    availableLanguages: ['en'],
    resources: [scaffolderTranslations, catalogImportTranslations],
  },
  plugins: [shortcutsPlugin, badgesPlugin],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    NotFoundErrorPage: () => (
      <CustomErrorPage status="404" statusMessage="Page not found" />
    ),
    SignInPage: SignInRouter,
  },
  themes: [
    {
      id: 'dlr-theme',
      title: 'DLR',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={dlrTheme} children={children} />
      ),
    },
  ],
  icons: simpleIconsColor,
});

const queryClient = new QueryClient();

function RootLandingRoute() {
  const configApi = useApi(configApiRef);
  const isNewLandingPage = configApi.getOptionalBoolean(
    'featureFlags.newLandingPage',
  );

  return isNewLandingPage ? <LandingPage /> : <HomePage />;
}

function SignInRouter(props: SignInPageProps) {
  const configApi = useApi(configApiRef);
  const useNewLandingPage = Boolean(
    configApi.getOptionalBoolean('featureFlags.newLandingPage'),
  );

  if (useNewLandingPage) {
    return <LandingSignInPage {...props} />;
  }

  const logtoAuthProvider = {
    id: 'logto-provider',
    title: 'DXM SSO',
    message: 'Sign in using DXM SSO',
    apiRef: logtoOIDCAuthApiRef,
  };

  return <SingleSignInPage {...props} provider={logtoAuthProvider} />;
}

function RootLayout({ children }: PropsWithChildren<{}>) {
  const configApi = useApi(configApiRef);
  const isNewLandingPage = configApi.getOptionalBoolean(
    'featureFlags.newLandingPage',
  );

  const LayoutRoot = isNewLandingPage ? RootWithLandingPageView : Root;

  return <LayoutRoot>{children}</LayoutRoot>;
}

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <RootLandingRoute />
    </Route>

    <Route path="/catalog" element={<CatalogIndexPage />}>
      <CustomCatalogIndex />
    </Route>

    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>

    <Route path="/docs" element={<TechDocsIndexPage />} />

    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>

    {/* Docs V2 landing and tab-specific routes */}
    <Route path="/docs-v2" element={<DocsPage />} />
    <Route
      path="/docs-v2/getting-started/:slug"
      element={
        <DocsArticle
          docsBasePath="/docs/landing-page-guide"
          tabId="getting-started"
        />
      }
    />
    <Route
      path="/docs-v2/landing-page-guide/:slug"
      element={
        <DocsArticle
          docsBasePath="/docs/landing-page-guide"
          tabId="landing-page-guide"
        />
      }
    />
    <Route path="/docs-v2/:tabId/:sectionId" element={<DocsPage />} />
    <Route path="/docs-v2/:tabId" element={<DocsPage />} />

    <Route
      path="/docs-v2/help/:sectionId/:slug"
      element={<DocsArticle docsBasePath="/docs/help" tabId="help" />}
    />
    <Route
      path="/docs-v2/release-notes/:slug"
      element={
        <DocsArticle docsBasePath="/docs/release-notes" tabId="release-notes" />
      }
    />

    <Route
      path="/create"
      element={
        <RequirePermission permission={dxPortalAdvancedFeaturesPermission}>
          <IdentityProvider>
            <>
              <ScaffolderPage
                headerOptions={{
                  title: 'Create a new Intake Request',
                  subtitle:
                    'Request integration of your app, API or SSO federation with DXM SSO with a simple form',
                }}
              />
              <ScaffolderReviewCleanup />
            </>
          </IdentityProvider>
        </RequirePermission>
      }
    >
      <ScaffolderFieldExtensions>
        <IdTokenFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>

    <Route
      path="/api-docs"
      element={
        <RequirePermission permission={dxPortalBasicFeaturesPermission}>
          <CatalogApiDocs />
        </RequirePermission>
      }
    />

    <Route
      path="/api-docs/:id"
      element={
        <RequirePermission permission={dxPortalBasicFeaturesPermission}>
          <ApiDefinitionPage />
        </RequirePermission>
      }
    />

    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={dxPortalAdvancedFeaturesPermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />

    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>

    <Route
      path="/settings"
      element={
        <IdentityProvider>
          <UserSettingsPage />
        </IdentityProvider>
      }
    >
      {SettingsPage}
    </Route>

    <Route
      path="/catalog-graph"
      element={
        <RequirePermission permission={dxPortalAdvancedFeaturesPermission}>
          <CatalogGraphPage />
        </RequirePermission>
      }
    />

    <Route
      path="/announcements"
      element={
        <RequirePermission permission={dxPortalAdvancedFeaturesPermission}>
          <ThemeProvider theme={announcementsPageTheme}>
            <AnnouncementsPage
              subtitle="Announcements appear on the Home page to inform users about
                  planned maintenance and other possible events applicable to
                  the DX Portal"
            />
          </ThemeProvider>
        </RequirePermission>
      }
    />

    <Route
      path="/devtools"
      element={
        <RequirePermission permission={dxPortalAdvancedFeaturesPermission}>
          <DevToolsPage />
        </RequirePermission>
      }
    >
      {customDevToolsPage}
    </Route>

    <Route
      path="/toolbox"
      element={
        <RequirePermission permission={dxPortalAdvancedFeaturesPermission}>
          <ToolboxPage
            toolFilterFunction={tool => tool.category !== 'Backstage'}
          />
        </RequirePermission>
      }
    />

    <Route
      path="/catalog-graph"
      element={
        <RequirePermission permission={dxPortalAdvancedFeaturesPermission}>
          <RelationsCatalogGraphPage />
        </RequirePermission>
      }
    />

    <Route
      path="/storybook"
      element={
        <RequirePermission permission={dxPortalAdvancedFeaturesPermission}>
          <DxIframeStorybookPage />
        </RequirePermission>
      }
    />

    <Route
      path="/system-status"
      element={
        <RequirePermission permission={dxPortalBasicFeaturesPermission}>
          <DxIframeSystemStatusPage />
        </RequirePermission>
      }
    />

    <Route
      path="/open-feedback"
      element={
        <RequirePermission permission={dxPortalAdvancedFeaturesPermission}>
          <OpenFeedbackPage />
        </RequirePermission>
      }
    />

    <Route
      path="/user-requests/*"
      element={
        <RequirePermission permission={dxPortalAdvancedFeaturesPermission}>
          <IdentityProvider>
            <UserRequestsPermissionGuard>
              <UserRequestsPage />
            </UserRequestsPermissionGuard>
          </IdentityProvider>
        </RequirePermission>
      }
    />

    <Route
      path="/user-management/*"
      element={
        <RequirePermission permission={dxPortalAdvancedFeaturesPermission}>
          <IdentityProvider>
            <UserManagement />
          </IdentityProvider>
        </RequirePermission>
      }
    />

    <Route
      path="/contact-us"
      element={
        <IdentityProvider>
          <ContactUsPage />
        </IdentityProvider>
      }
    />
  </FlatRoutes>
);

export default app.createRoot(
  <QueryClientProvider client={queryClient}>
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      autoHideDuration={3000}
      hideIconVariant
      SnackbarProps={{ style: { zIndex: 3000 } }}
      domRoot={document.getElementById('root') as HTMLElement}
    >
      <AlertDisplay />
      <OAuthRequestDialog />
      <AppRouter>
        <VisitListener />
        <IdentityProvider>
          <RootLayout>{routes}</RootLayout>
          <PraxoChatWidget />
        </IdentityProvider>
      </AppRouter>
    </SnackbarProvider>
  </QueryClientProvider>,
);
